import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  DeleteOutlined,
  EditOutlined,
  FileAddOutlined,
  FileOutlined,
  FolderAddOutlined,
  FolderOutlined,
  GlobalOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Card, Dropdown, MenuProps, Modal, Tree, message } from 'antd';
import type { DataNode, TreeProps } from 'antd/es/tree';
import { useTranslation } from 'react-i18next';

import Layout from 'components/Layout/Layout';

import EditContainerModal from 'scenes/Settings/NewReportAndMenus/components/EditContainerModal';
import EditReportModal from 'scenes/Settings/NewReportAndMenus/components/EditReportModal';
import NewContainerModal from 'scenes/Settings/NewReportAndMenus/components/NewContainerModal';
import NewReportModal from 'scenes/Settings/NewReportAndMenus/components/NewReportModal';

import { ApiErrorData } from 'models/ApiResponse';
import { MenuNode } from 'models/MenuNode';

import { MenuReorganizeSchema, MenuService } from 'services/MenuService';

import './NewReportAndMenus.scss';

function NewReportAndMenus() {
  const [modal, contextModalHolder] = Modal.useModal();
  const [messageApi, contextHolder] = message.useMessage();
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [treeLoading, setTreeLoading] = useState(true);
  const [datasource, setDatasource] = useState<MenuNode[]>([]);
  const datasourceRef = useRef<MenuNode[]>([]); // ✅ Ref para evitar re-render
  const [title] = useState<string>('');
  const [isOpenNewReportModal, setIsOpenNewReportModal] = useState(false);
  const [isOpenNewContainerModal, setIsOpenNewContainerModal] = useState(false);
  const [isOpenEditContainerModal, setIsOpenEditContainerModal] =
    useState(false);
  const [isOpenEditReportModal, setIsOpenEditReportModal] = useState(false);
  const [selectedNode, setSelectedNode] = useState<MenuNode | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const ROOT_CODE = 'virtual_root';

  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const hasInitializedExpanded = useRef(false);

  // 🔍 Buscar nodo por código
  const findMenuNodeByCode = (
    nodes: MenuNode[] = [],
    code: string
  ): MenuNode | null => {
    for (const n of nodes) {
      if (String(n.code) === String(code)) return n;
      if (Array.isArray(n.children) && n.children.length > 0) {
        const found = findMenuNodeByCode(n.children, code);
        if (found) return found;
      }
    }
    return null;
  };

  // ⚙️ Menú contextual
  const buildContextMenuItems = (
    isContainer: boolean,
    companyOwnerId?: any | null,
    isRoot = false
  ): MenuProps['items'] => {
    const items: MenuProps['items'] = [];
    const newGroupChildren: MenuProps['items'] = [];

    if (isRoot || isContainer) {
      newGroupChildren.push({
        key: 'addContainer',
        icon: <FolderAddOutlined />,
        label: t(
          'scenes.new_report_and_menus.scene.context_menu.new_container'
        ),
      });
      newGroupChildren.push({
        key: 'addReport',
        icon: <FileAddOutlined />,
        label: t('scenes.new_report_and_menus.scene.context_menu.new_report'),
      });
    }

    if (newGroupChildren.length > 0) {
      items.push({
        key: 'new',
        icon: <PlusOutlined />,
        label: t('scenes.new_report_and_menus.scene.context_menu.group_new'),
        children: newGroupChildren,
      });
    }

    if (companyOwnerId !== null && companyOwnerId !== undefined) {
      // Mostrar 'Editar' tanto para contenedores como reportes (más adelante abrirá otro modal)
      items.push({
        key: 'edit',
        icon: <EditOutlined />,
        label: t('scenes.new_report_and_menus.scene.context_menu.edit'),
      });
      // Eliminar aplica a ambos (contenedor o reporte)
      items.push({
        key: isContainer ? 'deleteContainer' : 'deleteReport',
        icon: <DeleteOutlined />,
        label: t('scenes.new_report_and_menus.scene.context_menu.delete'),
        danger: true,
      });
    }

    return items;
  };

  const onContextMenuClick = (nodeKey: string) => {
    return ({ key }: { key: string }) => {
      const parentNode =
        nodeKey === ROOT_CODE
          ? null
          : findMenuNodeByCode(datasourceRef.current || [], String(nodeKey));

      switch (key) {
        case 'addContainer':
          setSelectedNode(parentNode);
          setIsOpenNewContainerModal(true);
          break;
        case 'addReport':
          setSelectedNode(parentNode);
          setIsOpenNewReportModal(true);
          break;
        case 'edit': {
          const node =
            nodeKey === ROOT_CODE
              ? null
              : findMenuNodeByCode(
                  datasourceRef.current || [],
                  String(nodeKey)
                );
          if (!node) return;
          setSelectedNode(node);
          // Solo editar contenedores; para reportes mostramos aviso.
          const isContainer = node.menuTypeId === 'PARENT_MENU';
          if (isContainer) {
            setIsOpenEditContainerModal(true);
          } else {
            setIsOpenEditReportModal(true);
          }
          break;
        }
        case 'deleteContainer':
          handleDeleteBranch(String(nodeKey), true);
          break;
        case 'deleteReport':
          handleDeleteBranch(String(nodeKey), false);
          break;
        default:
          break;
      }
    };
  };

  // 🌐 Nodo raíz
  const INIT_TREE_ROOT: DataNode = useMemo(
    () => ({
      title: (
        <Dropdown
          trigger={['contextMenu']}
          menu={{
            items: buildContextMenuItems(false, null, true),
            onClick: onContextMenuClick(String(ROOT_CODE)),
            className: 'settings-context-menu',
          }}
          overlayClassName="settings-context-menu"
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
            }}
            onContextMenu={(e) => {
              e.stopPropagation();
              setSelectedKeys([ROOT_CODE]);
              setSelectedNode(null);
            }}
          >
            <GlobalOutlined />
            <span style={{ fontWeight: 550 }}>{window.location.origin}</span>
          </div>
        </Dropdown>
      ),
      key: ROOT_CODE,
      children: [],
    }),
    [i18n.language]
  );

  // 🗂️ Mapear MenuNode → DataNode
  const mapNodeToTree = (node: MenuNode): DataNode => {
    const isParent = node.menuTypeId === 'PARENT_MENU';
    const isCompanyOwned =
      node.companyOwnerId !== null && node.companyOwnerId !== undefined;
    const localizedTitle =
      node.localizedTitles?.[i18n.language as 'es' | 'en'] || node.title;

    const label = (
      <span
        style={
          isCompanyOwned ? { color: '#1677ff', fontWeight: 550 } : undefined
        }
      >
        {localizedTitle}
      </span>
    );

    const iconStyle = isCompanyOwned ? { color: '#1677ff' } : undefined;
    const iconEl = isParent ? (
      <FolderOutlined style={iconStyle} />
    ) : (
      <FileOutlined style={iconStyle} />
    );

    const title = (
      <Dropdown
        trigger={['contextMenu']}
        menu={{
          items: buildContextMenuItems(isParent, node.companyOwnerId, false),
          onClick: onContextMenuClick(String(node.code)),
          className: 'settings-context-menu',
        }}
        overlayClassName="settings-context-menu"
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
          }}
          onContextMenu={(e) => {
            e.stopPropagation();
            setSelectedKeys([String(node.code)]);
            setSelectedNode(node);
          }}
        >
          {iconEl}
          {label}
        </div>
      </Dropdown>
    );

    const children: DataNode[] = Array.isArray(node.children)
      ? node.children.map((child) => mapNodeToTree(child))
      : [];

    return { title, key: String(node.code), children };
  };

  // 📦 Fetch inicial
  useEffect(() => {
    (async () => {
      await fetchDataFromServer(true);
      setLoading(false);
    })();
  }, []);

  const fetchDataFromServer = async (withLoading = false) => {
    if (withLoading) setTreeLoading(true);
    const response = await MenuService.getMenuNodesByCurrentCompany();

    if (response.success) {
      const dataTree = response.data as MenuNode[];
      datasourceRef.current = dataTree;
      setDatasource(dataTree);
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        error.message ||
          t('scenes.new_report_and_menus.scene.errors.fetch_menus'),
        3.5
      );
    }
    if (withLoading) setTreeLoading(false);
  };

  const [treeData, setTreeData] = useState<DataNode[]>([INIT_TREE_ROOT]);
  const [nodeTypeByKey, setNodeTypeByKey] = useState<Record<string, string>>({
    [ROOT_CODE]: 'ROOT',
  });
  const treeBackupRef = useRef<DataNode[]>([INIT_TREE_ROOT]);

  // 🧩 Clonar treeData sin crear nuevos elementos React en title
  const cloneTreeData = (nodes: DataNode[]): DataNode[] => {
    const clone = (list: DataNode[]): DataNode[] =>
      list.map((node) => ({
        ...node,
        children: Array.isArray(node.children)
          ? clone(node.children as DataNode[])
          : undefined,
      }));
    return clone(nodes);
  };

  const getAllKeys = (nodes: DataNode[]): React.Key[] => {
    const keys: React.Key[] = [];
    const traverse = (list: DataNode[]) => {
      list.forEach((n) => {
        keys.push(n.key);
        if (Array.isArray(n.children) && n.children.length > 0) {
          traverse(n.children);
        }
      });
    };
    traverse(nodes);
    return keys;
  };

  // 🌳 Render inicial del árbol
  useEffect(() => {
    const children = (datasource || []).map((node) => mapNodeToTree(node));
    const newTreeData: DataNode[] = [{ ...INIT_TREE_ROOT, children }];
    setTreeData(newTreeData);

    const buildTypeMap = (
      nodes: MenuNode[],
      map: Record<string, string> = {}
    ) => {
      nodes.forEach((n) => {
        map[n.code] = n.menuTypeId;
        if (Array.isArray(n.children) && n.children.length > 0)
          buildTypeMap(n.children, map);
      });
      return map;
    };

    setNodeTypeByKey({
      [ROOT_CODE]: 'ROOT',
      ...buildTypeMap(datasource || []),
    });

    if (!hasInitializedExpanded.current) {
      const hasChildren =
        Array.isArray(newTreeData[0].children) &&
        newTreeData[0].children.length > 0;
      if (hasChildren) {
        const allKeys = getAllKeys(newTreeData);
        setExpandedKeys(allKeys);
        hasInitializedExpanded.current = true;
      }
    }
  }, [datasource, i18n.language]);

  const onExpand: TreeProps['onExpand'] = (keys) => setExpandedKeys(keys);

  // ➕ Añadir nodo sin re-render global
  const addNodeToTree = (
    newNode: MenuNode,
    treeData: DataNode[],
    setTreeData: React.Dispatch<React.SetStateAction<DataNode[]>>,
    setSelectedKeys: React.Dispatch<React.SetStateAction<React.Key[]>>,
    setExpandedKeys: React.Dispatch<React.SetStateAction<React.Key[]>>,
    setNodeTypeByKey: React.Dispatch<
      React.SetStateAction<Record<string, string>>
    >
  ) => {
    // Actualizar datasourceRef sin disparar re-render
    const updateDatasourceRef = (nodes: MenuNode[]): boolean => {
      for (const n of nodes) {
        if (String(n.code) === String(newNode.parentMenuCode ?? ROOT_CODE)) {
          n.children = Array.isArray(n.children)
            ? [...n.children, newNode]
            : [newNode];
          return true;
        }
        if (n.children && updateDatasourceRef(n.children)) return true;
      }
      return false;
    };
    const inserted = updateDatasourceRef(datasourceRef.current);
    if (!inserted) datasourceRef.current.push(newNode);

    // Agregar visualmente al árbol
    const insertRecursively = (nodes: DataNode[]): boolean => {
      for (const node of nodes) {
        if (String(node.key) === String(newNode.parentMenuCode ?? ROOT_CODE)) {
          const children = Array.isArray(node.children)
            ? [...node.children]
            : [];
          const newTreeNode = mapNodeToTree(newNode);
          children.push(newTreeNode);
          node.children = children;
          return true;
        }
        if (node.children && insertRecursively(node.children as DataNode[])) {
          return true;
        }
      }
      return false;
    };

    const updatedTree = [...treeData];
    const ok = insertRecursively(updatedTree);
    if (ok) {
      setTreeData(updatedTree);
      const parentKey = newNode.parentMenuCode ?? ROOT_CODE;
      setExpandedKeys((prev) =>
        prev.includes(parentKey) ? prev : [...prev, parentKey]
      );
      setSelectedKeys([newNode.code]);

      // Actualizar nodeTypeByKey
      setNodeTypeByKey((prev) => ({
        ...prev,
        [newNode.code]: newNode.menuTypeId,
      }));
    }
  };

  const handleNewMenuCreated = (createdNode: MenuNode) => {
    addNodeToTree(
      createdNode,
      treeData,
      setTreeData,
      setSelectedKeys,
      setExpandedKeys,
      setNodeTypeByKey
    );
  };

  // 🗑️ Confirmar eliminación y registrar datos (sin eliminar visualmente)
  const getDescendantCodes = (node?: MenuNode | null): string[] => {
    if (!node || !Array.isArray(node.children)) return [];
    const result: string[] = [];
    const traverse = (children?: MenuNode[]) => {
      if (!children) return;
      children.forEach((child) => {
        result.push(String(child.code));
        if (Array.isArray(child.children) && child.children.length > 0) {
          traverse(child.children);
        }
      });
    };
    traverse(node.children);
    return result;
  };

  // 🧪 Valida descendientes que BLOQUEAN eliminación de contenedor
  // Condición solicitada: se puede eliminar si companyOwnerId !== null
  // Por lo tanto, bloquean los que tienen companyOwnerId === null
  // Devuelve nombres amigables de los menús que bloquean la acción
  const getBlockingDescendantNames = (node?: MenuNode | null): string[] => {
    if (!node || !Array.isArray(node.children)) return [];
    const blockers: string[] = [];
    const traverse = (children?: MenuNode[]) => {
      if (!children) return;
      children.forEach((child) => {
        const owner = (child as any).companyOwnerId;
        if (owner === null || owner === undefined) {
          const lang = i18n.language as 'es' | 'en';
          const displayName =
            (child.localizedTitles && child.localizedTitles[lang]) ||
            child.title ||
            String(child.code);
          blockers.push(displayName);
        }
        if (Array.isArray(child.children) && child.children.length > 0) {
          traverse(child.children);
        }
      });
    };
    traverse(node.children);
    return blockers;
  };

  const handleDeleteBranch = (nodeKey: string, isContainer: boolean) => {
    const node =
      nodeKey === ROOT_CODE
        ? null
        : findMenuNodeByCode(datasourceRef.current || [], String(nodeKey));

    const title = t('scenes.new_report_and_menus.scene.delete_confirm.title');
    const content = isContainer
      ? t('scenes.new_report_and_menus.scene.delete_confirm.content_container')
      : t('scenes.new_report_and_menus.scene.delete_confirm.content_report');

    modal.confirm({
      title,
      content,
      okText: t('scenes.new_report_and_menus.scene.delete_confirm.ok'),
      cancelText: t('scenes.new_report_and_menus.scene.delete_confirm.cancel'),
      onOk: async () => {
        try {
          // Validación: si es contenedor, todos los descendientes deben tener companyOwnerId === null
          if (isContainer) {
            const blockingNames = getBlockingDescendantNames(node);
            if (blockingNames.length > 0) {
              messageApi.error(
                t(
                  'scenes.new_report_and_menus.scene.errors.delete_container_blocked',
                  { names: blockingNames.join(', ') }
                )
              );
              return;
            }
          }

          const codeToDelete = String(nodeKey);
          const descendants = isContainer ? getDescendantCodes(node) : [];
          // Solo registro en consola; no se elimina nada por ahora
          // Formato: string[] consecutivo => [codigo_padre, ...codigos_hijos_en_DFS]
          const menuCodesForDelete = [codeToDelete, ...descendants];
          const response = await MenuService.deleteCompanyOwnerMenus(
            menuCodesForDelete
          );

          if (response.success) {
            removeNodeFromTreeAndDatasource(
              codeToDelete,
              treeData,
              setTreeData,
              datasourceRef,
              setDatasource,
              setSelectedKeys,
              setExpandedKeys,
              setNodeTypeByKey,
              node
            );
          } else {
            const errorData = response.data as ApiErrorData;
            // Error controlado del servidor
            messageApi.error(
              errorData?.message ||
                t(
                  'scenes.new_report_and_menus.scene.errors.delete_menu_generic'
                ),
              3.5
            );
          }
        } catch (err: any) {
          // Error no controlado
          messageApi.error(
            err?.message ||
              t('scenes.new_report_and_menus.scene.errors.delete_menu_generic'),
            3.5
          );
        }
      },
    });
  };

  const updateNodeInTreeAndDatasource = (
    updatedNode: MenuNode,
    treeData: DataNode[],
    setTreeData: React.Dispatch<React.SetStateAction<DataNode[]>>,
    datasource: MenuNode[],
    setDatasource: React.Dispatch<React.SetStateAction<MenuNode[]>>
  ) => {
    // --- 1️⃣ Actualiza en treeData (reemplazando el title por el JSX correcto) ---
    setTreeData((prev) => {
      const cloned = cloneTreeData(prev);

      const updateTreeRecursively = (nodes: DataNode[]): boolean => {
        for (const node of nodes) {
          if (String(node.key) === String(updatedNode.code)) {
            // Reconstruir el title usando mapNodeToTree para mantener dropdown, iconos y estilos
            const reconstructed = mapNodeToTree(updatedNode);
            node.title = reconstructed.title;
            // si además quieres mantener children visuales tal cual, no los toques aquí
            return true;
          }
          if (
            node.children &&
            updateTreeRecursively(node.children as DataNode[])
          ) {
            return true;
          }
        }
        return false;
      };

      updateTreeRecursively(cloned);
      return cloned;
    });

    // --- 2️⃣ Actualiza datasourceRef.current (manteniendo la referencia consistente) ---
    const updateDatasourceRecursivelyInRef = (nodes: MenuNode[]): boolean => {
      for (const n of nodes) {
        if (String(n.code) === String(updatedNode.code)) {
          // actualizar campos relevantes (no reasignes el objeto si hay refs que lo usan)
          n.title = updatedNode.title;
          n.localizedTitles = updatedNode.localizedTitles ?? null;
          n.menuTypeId = updatedNode.menuTypeId ?? n.menuTypeId;
          // si hay más campos que puedan cambiar, actualízalos aquí
          return true;
        }
        if (n.children && updateDatasourceRecursivelyInRef(n.children)) {
          return true;
        }
      }
      return false;
    };

    // intenta actualizar en el ref; si no está (por algún motivo), inserta/ignore
    updateDatasourceRecursivelyInRef(datasourceRef.current);

    // actualizar el estado datasource (para que la UI que dependa del estado se actualice)
    setDatasource(structuredClone(datasourceRef.current));

    // --- 3️⃣ (opcional) actualizar nodeTypeByKey si cambió el tipo ---
    // si el menuTypeId pudo cambiar, actualiza el mapa de tipos:
    setNodeTypeByKey((prev) => {
      if (prev[String(updatedNode.code)] === updatedNode.menuTypeId)
        return prev;
      return { ...prev, [String(updatedNode.code)]: updatedNode.menuTypeId };
    });
  };

  const handleNodeUpdated = (node: MenuNode) => {
    updateNodeInTreeAndDatasource(
      node,
      treeData,
      setTreeData,
      datasource,
      setDatasource
    );
  };

  // 🗑️ Eliminar nodo y su subárbol del treeData y datasource sin rerender global
  const removeNodeFromTreeAndDatasource = (
    codeToDelete: string,
    treeData: DataNode[],
    setTreeData: React.Dispatch<React.SetStateAction<DataNode[]>>,
    datasourceRef: React.MutableRefObject<MenuNode[]>,
    setDatasource: React.Dispatch<React.SetStateAction<MenuNode[]>>,
    setSelectedKeys: React.Dispatch<React.SetStateAction<React.Key[]>>,
    setExpandedKeys: React.Dispatch<React.SetStateAction<React.Key[]>>,
    setNodeTypeByKey: React.Dispatch<
      React.SetStateAction<Record<string, string>>
    >,
    originalNode: MenuNode | null
  ) => {
    // 1) Calcular códigos a remover (padre + descendientes)
    const codesToRemove: string[] = [codeToDelete];
    if (originalNode) {
      codesToRemove.push(...getDescendantCodes(originalNode));
    }

    // 2) Eliminar del datasourceRef y actualizar estado
    const removeFromDatasource = (list: MenuNode[]): boolean => {
      for (let i = 0; i < list.length; i++) {
        if (String(list[i].code) === String(codeToDelete)) {
          list.splice(i, 1);
          return true;
        }
        if (list[i].children && removeFromDatasource(list[i].children)) {
          return true;
        }
      }
      return false;
    };
    removeFromDatasource(datasourceRef.current);
    setDatasource(structuredClone(datasourceRef.current));

    // 3) Eliminar del treeData (visual)
    const removeFromTree = (nodes: DataNode[]): boolean => {
      for (let i = 0; i < nodes.length; i++) {
        if (String(nodes[i].key) === String(codeToDelete)) {
          nodes.splice(i, 1);
          return true;
        }
        if (
          nodes[i].children &&
          removeFromTree(nodes[i].children as DataNode[])
        ) {
          return true;
        }
      }
      return false;
    };
    setTreeData((prev) => {
      const cloned = cloneTreeData(prev);
      removeFromTree(cloned);
      return cloned;
    });

    // 4) Limpiar selección, expansión y mapa de tipos
    setSelectedKeys((prev) =>
      prev.filter((k) => !codesToRemove.includes(String(k)))
    );
    setExpandedKeys((prev) =>
      prev.filter((k) => !codesToRemove.includes(String(k)))
    );
    setNodeTypeByKey((prev) => {
      const next = { ...prev };
      codesToRemove.forEach((c) => delete next[c]);
      return next;
    });
  };

  const onDrop: TreeProps['onDrop'] = (info) => {
    // 1️⃣ Hacer backup del árbol por si falla la actualización
    treeBackupRef.current = cloneTreeData(treeData);

    const dropKey = info.node.key as React.Key;
    const dragKey = info.dragNode.key as React.Key;

    // Nota: el bloqueo de anidar en ROOT se maneja visualmente con Tree.draggable.allowDrop

    const targetType = nodeTypeByKey[String(dropKey)];
    // Permitir anidar también dentro del ROOT virtual
    const canNest =
      targetType === 'PARENT_MENU' || String(dropKey) === String(ROOT_CODE);
    if (!info.dropToGap && !canNest) {
      info.dropToGap = true;
    }

    const dropPos = info.node.pos.split('-');
    let dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);
    if (info.dropToGap && dropPosition === 0) dropPosition = 1;

    // 2️⃣ Función auxiliar para recorrer y remover/inserta nodes en treeData
    const loop = (
      nodes: DataNode[],
      key: React.Key,
      callback: (node: DataNode, index: number, arr: DataNode[]) => void
    ) => {
      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].key === key) return callback(nodes[i], i, nodes);
        if (nodes[i].children)
          loop(nodes[i].children as DataNode[], key, callback);
      }
    };

    const data = [...treeData];
    let dragObj: DataNode | undefined;

    // 3️⃣ Remover nodo arrastrado
    loop(data, dragKey, (item, index, arr) => {
      arr.splice(index, 1);
      dragObj = item;
    });

    if (!dragObj) return;

    // 4️⃣ Insertar nodo según dropToGap y dropPosition
    if (!info.dropToGap) {
      loop(data, dropKey, (item) => {
        item.children = item.children || [];
        (item.children as DataNode[]).splice(0, 0, dragObj as DataNode); // insert as first child
      });
    } else {
      let arr: DataNode[] = [];
      let i = 0;
      loop(data, dropKey, (_item, index, parentArr) => {
        arr = parentArr;
        i = index;
      });
      if (dropPosition === -1) arr.splice(i, 0, dragObj as DataNode);
      else arr.splice(i + 1, 0, dragObj as DataNode);
    }

    setTreeData(data);

    // 5️⃣ Actualizar datasourceRef de manera sincronizada
    const updateDatasourceRef = (
      nodes: MenuNode[],
      dragKey: string,
      dropKey: string | null,
      dropPosition: number,
      isDropToGap: boolean
    ) => {
      let dragNode: MenuNode | null = null;

      const removeNode = (list: MenuNode[]): boolean => {
        for (let i = 0; i < list.length; i++) {
          if (String(list[i].code) === dragKey) {
            dragNode = list.splice(i, 1)[0];
            return true;
          }
          if (list[i].children && removeNode(list[i].children)) return true;
        }
        return false;
      };

      const insertNode = (list: MenuNode[]): boolean => {
        for (let i = 0; i < list.length; i++) {
          const node = list[i];
          if (String(node.code) === dropKey) {
            node.children = node.children || [];
            if (!isDropToGap) {
              node.children.splice(0, 0, dragNode as MenuNode);
            } else {
              if (dropPosition === -1) list.splice(i, 0, dragNode as MenuNode);
              else list.splice(i + 1, 0, dragNode as MenuNode);
            }
            return true;
          }
          if (node.children && insertNode(node.children)) return true;
        }
        return false;
      };

      removeNode(nodes);
      if (!dragNode) return;

      // No insertar nunca en el nivel raíz
      if (dropKey === ROOT_CODE) return;
      insertNode(nodes);
    };

    updateDatasourceRef(
      datasourceRef.current,
      String(dragKey),
      String(dropKey),
      dropPosition,
      info.dropToGap
    );

    // 6️⃣ Flatten tree para enviar a backend
    const flattenUpdatedNodes = (tree: DataNode[]): MenuReorganizeSchema[] => {
      const result: MenuReorganizeSchema[] = [];

      const traverse = (nodes: DataNode[], parentCode: string | null) => {
        nodes.forEach((node, index) => {
          const keyStr = String(node.key);
          if (keyStr === ROOT_CODE) {
            traverse((node.children || []) as DataNode[], null);
            return;
          }

          result.push({
            code: keyStr,
            order: index + 1,
            parentCode,
          });

          if (Array.isArray(node.children) && node.children.length > 0) {
            traverse(node.children as DataNode[], keyStr);
          }
        });
      };

      traverse(tree, null);
      return result;
    };

    const updatedNodes = flattenUpdatedNodes(data);
    updateMenuNodesProcess(updatedNodes);
  };

  const updateMenuNodesProcess = async (
    updatedNodes: MenuReorganizeSchema[]
  ) => {
    setTreeLoading(true);
    try {
      const response = await MenuService.reorganizeCompanyMenus({
        menus: updatedNodes,
      });

      if (!response.success) {
        throw new Error('Error al intententar actualizar los valores.');
      }

      treeBackupRef.current = cloneTreeData(treeData);
    } catch (err) {
      messageApi.error(
        t('scenes.new_report_and_menus.scene.errors.reorganize_failed_restore'),
        3.5
      );
      setTreeData(treeBackupRef.current);
    } finally {
      setTreeLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      {contextModalHolder}

      <Layout title={title} loading={treeLoading}>
        <Card loading={loading} onContextMenu={(e) => e.preventDefault()}>
          <Tree
            showIcon
            showLine
            selectable
            selectedKeys={selectedKeys}
            expandedKeys={expandedKeys}
            onExpand={onExpand}
            blockNode
            draggable
            allowDrop={({ dropNode, dropPosition }) => {
              const keyStr = String(dropNode.key);
              // ROOT: bloquear cualquier interacción (gap y anidar)
              if (
                keyStr === String(ROOT_CODE) &&
                [1, -1].includes(dropPosition)
              ) {
                return false;
              }

              // Otros nodos: permitir anidar solo dentro de contenedores
              if (dropPosition === 0) {
                const type = nodeTypeByKey[keyStr];
                return type === 'PARENT_MENU' || keyStr === String(ROOT_CODE);
              }
              // Permitir mover entre elementos (gap) en cualquier otro nivel
              return true;
            }}
            onDrop={onDrop}
            treeData={treeData}
          />
        </Card>

        <NewContainerModal
          isOpen={isOpenNewContainerModal}
          handleModalCancel={() => setIsOpenNewContainerModal(false)}
          parentNode={selectedNode}
          handleNewMenuCreated={handleNewMenuCreated}
        />

        <NewReportModal
          isOpen={isOpenNewReportModal}
          handleModalCancel={() => setIsOpenNewReportModal(false)}
          parentNode={selectedNode}
          handleNewMenuCreated={handleNewMenuCreated}
        />

        {isOpenEditContainerModal && selectedNode?.code && (
          <EditContainerModal
            isOpen={isOpenEditContainerModal}
            handleModalCancel={() => setIsOpenEditContainerModal(false)}
            code={String(selectedNode.code)}
            handleNodeUpdated={handleNodeUpdated}
          />
        )}

        {isOpenEditReportModal && selectedNode?.code && (
          <EditReportModal
            isOpen={isOpenEditReportModal}
            handleModalCancel={() => setIsOpenEditReportModal(false)}
            code={String(selectedNode.code)}
            handleNodeUpdated={handleNodeUpdated}
          />
        )}
      </Layout>
    </>
  );
}

export default NewReportAndMenus;
